import { useMemo, useState } from "react";
import { CopyCode } from "@/components/CopyCode";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { formatBs, formatUsd, formatCop, formatDate } from "@/lib/format";
import { StatusBadge } from "@/pages/admin/DashboardPage";

// Modalidades solicitadas por QA: figuras / terminales / triples → familia real
const MODALITIES = [
  { key: "animalitos", label: "Figuras" },
  { key: "chance", label: "Terminales" },
  { key: "tradicional", label: "Triples" },
];

const CURRENCY_BADGE = {
  BS: "border-primary/40 bg-primary/10 text-primary",
  USD: "border-emerald-600/40 bg-emerald-600/10 text-emerald-700 dark:text-emerald-300",
  COP: "border-[#8f1433] bg-[#8f1433] text-white",
};

const CURRENCIES = ["BS", "USD", "COP"];

// Formato exclusivo por divisa de emisión (cero conversión dual).
const fmtIn = (cur, n) => {
  if (cur === "COP") return `$ ${formatCop(n)} COP`;
  if (cur === "USD") return `$ ${formatUsd(n)} USD`;
  return `Bs ${formatBs(n)}`;
};
const field = (cur, base) => `${base}_${(cur || "BS").toLowerCase()}`;
const emittedAmount = (t) => fmtIn(t.currency, t[field(t.currency, "total")]);
const emittedPrize = (t) => fmtIn(t.currency, t[field(t.currency, "payout_total")]);

// Fecha de hoy en America/Caracas (YYYY-MM-DD).
const todayCaracas = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Caracas" });

const STATUS_TABS = [
  { key: "", label: "Todos" },
  { key: "active", label: "Vendidos" },
  { key: "void", label: "Anulados" },
  { key: "premiados", label: "Premiados" },
  { key: "winner", label: "Pendientes por pagar" },
  { key: "paid", label: "Pagados" },
];

function CurrencyTotals({ tickets }) {
  // Tres acumuladores desacoplados; los anulados no suman.
  const totals = useMemo(() => {
    const acc = { BS: 0, USD: 0, COP: 0 };
    for (const t of tickets) {
      const cur = t.currency || "BS";
      if (t.status === "void" || !(cur in acc)) continue;
      acc[cur] += Number(t[field(cur, "total")]) || 0;
    }
    return acc;
  }, [tickets]);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3" data-testid="pos-history-totals">
      {CURRENCIES.map((c) => (
        <Card key={c} className="bg-card border-border p-3" data-testid={`pos-history-total-${c.toLowerCase()}`}>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Total {c === "BS" ? "Bs" : c}</div>
          <div className="text-xl font-semibold tabular-nums" data-testid={`pos-history-total-${c.toLowerCase()}-value`}>{fmtIn(c, totals[c])}</div>
        </Card>
      ))}
    </div>
  );
}

export default function PosHistoryPage() {
  const [status, setStatus] = useState("");
  const [gameFamily, setGameFamily] = useState("");
  const [currency, setCurrency] = useState("");
  const [dateFrom, setDateFrom] = useState(todayCaracas);
  const [dateTo, setDateTo] = useState(todayCaracas);

  const { data: tickets = [], isLoading, isError, refetch: refetchTickets } = useQuery({
    queryKey: ["tickets", "pos-history", status, gameFamily, currency, dateFrom, dateTo],
    queryFn: async () => {
      const params = { limit: 500 };
      if (status) params.status = status;
      if (gameFamily) params.game_family = gameFamily;
      if (currency) params.currency = currency;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      return (await api.get("/tickets", { params })).data;
    },
    refetchInterval: 20000,
  });

  return (
    <div className="p-3 md:p-4" data-testid="pos-reports-page">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Historial de Ticket</h1>
          <p className="text-sm text-muted-foreground" data-testid="pos-historial-subtitle">(ticket)</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetchTickets()} data-testid="pos-history-refresh-button">
          <RefreshCw size={15} className="mr-1.5"/>Actualizar
        </Button>
      </div>

      {/* Filtros: rango de fechas (Caracas) + moneda + tipo de juego */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="pos-history-filters">
        <div className="space-y-1">
          <Label className="text-xs">Desde</Label>
          <Input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} data-testid="pos-history-date-from"/>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Hasta</Label>
          <Input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} data-testid="pos-history-date-to"/>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Moneda</Label>
          <Select value={currency || "all"} onValueChange={(v) => setCurrency(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="pos-history-currency-filter"><SelectValue placeholder="TODAS"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="pos-history-currency-option-all">TODAS</SelectItem>
              {CURRENCIES.map((c) => <SelectItem key={c} value={c} data-testid={`pos-history-currency-option-${c.toLowerCase()}`}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tipo de juego</Label>
          <Select value={gameFamily || "all"} onValueChange={(v) => setGameFamily(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="pos-report-game-family"><SelectValue placeholder="Todos"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {MODALITIES.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="pos-report-status-tabs">
        {STATUS_TABS.map((s) => (
          <button
            key={s.key || "all"}
            onClick={() => setStatus(s.key)}
            data-testid={`pos-report-status-${s.key || "all"}`}
            className={`rounded-full border-2 px-3.5 py-1.5 text-xs font-bold transition-colors ${status === s.key ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-secondary/60"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <CurrencyTotals tickets={tickets}/>

      <div className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-2" data-testid="pos-historial-ticket-title">Historial de ticket</div>
      <Card className="bg-card border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted-foreground text-left">
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Jugadas</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Moneda</th>
                <th className="px-4 py-3 text-right">Monto</th>
                <th className="px-4 py-3 text-right">Premio</th>
              </tr>
            </thead>
            <tbody data-testid="pos-report-tickets">
              {isLoading && <tr><td colSpan={8} className="px-4 py-6 text-muted-foreground">Cargando…</td></tr>}
              {isError && <tr><td colSpan={8} className="px-4 py-6 text-destructive" data-testid="pos-history-error">No se pudo cargar el historial. Verifique el rango de fechas.</td></tr>}
              {!isLoading && !isError && tickets.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground" data-testid="pos-history-empty">Sin tickets para el filtro seleccionado.</td></tr>}
              {tickets.map((t) => (
                <tr key={t.id} className="border-t border-border/60 hover:bg-secondary/40" data-testid={`pos-history-row-${t.code}`}>
                  <td className="px-4 py-2 font-mono text-primary"><CopyCode code={t.code} /></td>
                  <td className="px-4 py-2">{formatDate(t.created_at)}</td>
                  <td className="px-4 py-2 text-muted-foreground">{t.customer_name || "-"}</td>
                  <td className="px-4 py-2">{t.plays?.length}</td>
                  <td className="px-4 py-2"><StatusBadge status={t.status}/></td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-bold ${CURRENCY_BADGE[t.currency] || CURRENCY_BADGE.BS}`} data-testid={`pos-history-currency-${t.code}`}>{t.currency || "BS"}</span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold" data-testid={`pos-history-amount-${t.code}`}>{emittedAmount(t)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-amber-600 dark:text-amber-300" data-testid={`pos-history-prize-${t.code}`}>{emittedPrize(t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
